import {useTranslation} from '@/translations/i18n'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {Text} from '@/components/ui/text/Text'
import {AdministrativeActivitiesForm} from '@/features/projects/components/administrative-activities-form/AdministrativeActivitiesForm'
import {NotesFormProvider} from '@/features/projects/components/notes-form-provider/NotesFormProvider'
import {HttpGetProjectNotesResponse} from '@/features/projects/http/projects.http'
import {StyledAdministrativeActivitiesView, StyledAttachmentButton} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {CardInEditModeType} from '@/pages/project-details-notes/ProjectDetailsNotes'
import {useMemo} from 'react'
import {ProjectNotesType} from '../../types'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useOutletContext} from 'react-router-dom'

interface AdministrativeActivitiesSectionProps {
    administrativeActivity?: HttpGetProjectNotesResponse
    cardInEditMode: CardInEditModeType
    setCardInEditMode: (cardInEditMode: CardInEditModeType | null) => void
    isLoading: boolean
}
export const AdministrativeActivitiesSection = ({
    administrativeActivity,
    cardInEditMode,
    setCardInEditMode,
    isLoading
}: AdministrativeActivitiesSectionProps) => {
    const {t} = useTranslation()
    const {isEditable} = useOutletContext<{isEditable: boolean}>()
    const handleCancel = () => {
        setCardInEditMode(null)
    }

    const handleEdit = () => {
        setCardInEditMode('AdministrativeActivitiesSection')
    }
    const isActive = cardInEditMode === 'AdministrativeActivitiesSection'

    const defaultValues = useMemo(() => {
        return {
            aslFirstAttachment: administrativeActivity?.firstAttachment,
            aslSecondAttachment: administrativeActivity?.secondAttachment,
            notesDescription: administrativeActivity?.note,
            notesType: ProjectNotesType.enum.administrative_activities
        }
    }, [administrativeActivity])

    if (isActive) {
        return (
            <NotesFormProvider defaultValues={defaultValues}>
                <AdministrativeActivitiesForm setCardInEditMode={setCardInEditMode} />
            </NotesFormProvider>
        )
    }

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:administrative_activity')}
            isDisabled={cardInEditMode === 'OperationalNotesSection'}
            headingEndSlot={
                isEditable && (
                    <EditDetailsSectionButtons
                        isActive={isActive}
                        isDisabled={isLoading}
                        onCancel={handleCancel}
                        onEdit={handleEdit}
                    />
                )
            }
        >
            <StyledAdministrativeActivitiesView>
                <Flexbox direction="column" gap={2}>
                    <Flexbox gap={1}>
                        <Text size="textSm">{t('projects:asl_authorization')}</Text>
                        <Text size="textSm" palette="neutral" shade="500" weight="400">
                            {t('projects:attachment_x', {count: 1})}
                        </Text>
                    </Flexbox>
                    {isLoading ? (
                        <Skeleton height={20} />
                    ) : administrativeActivity?.firstAttachment ? (
                        <StyledAttachmentButton
                            title={administrativeActivity.firstAttachment.name}
                            to={administrativeActivity.firstAttachment.url}
                            target="_blank"
                            variant="tertiary"
                            shape="text"
                        >
                            {administrativeActivity.firstAttachment.name}
                        </StyledAttachmentButton>
                    ) : (
                        <Text palette="neutral" shade="400" size="textSm" weight="500">
                            {t('projects:no_attachment')}
                        </Text>
                    )}
                </Flexbox>
                <Flexbox direction="column" gap={2}>
                    <Flexbox gap={1}>
                        <Text size="textSm">{t('projects:asl_authorization')}</Text>
                        <Text size="textSm" palette="neutral" shade="500" weight="400">
                            {t('projects:attachment_x', {count: 2})}
                        </Text>
                    </Flexbox>
                    {isLoading ? (
                        <Skeleton height={20} />
                    ) : administrativeActivity?.secondAttachment ? (
                        <StyledAttachmentButton
                            title={administrativeActivity.secondAttachment.name}
                            to={administrativeActivity.secondAttachment.url}
                            target="_blank"
                            variant="tertiary"
                            shape="text"
                        >
                            {administrativeActivity.secondAttachment.name}
                        </StyledAttachmentButton>
                    ) : (
                        <Text palette="neutral" shade="400" size="textSm" weight="500">
                            {t('projects:no_attachment')}
                        </Text>
                    )}
                </Flexbox>
                <Flexbox direction="column" gap={2}>
                    {isLoading ? (
                        <Skeleton height={20} />
                    ) : administrativeActivity?.note ? (
                        <Text palette="neutral" shade="900" size="textSm" weight="400">
                            {administrativeActivity.note}
                        </Text>
                    ) : (
                        <Text palette="neutral" shade="400" size="textSm" weight="500">
                            {t('commons:no_note')}
                        </Text>
                    )}
                </Flexbox>
            </StyledAdministrativeActivitiesView>
        </DetailsFieldsSection>
    )
}
